import { userInvalidations, userQueries } from '@entities/User/queries';
import { UserFront } from '@entities/User/types';
import getRoute from '@root/src/services/routeList';
import apiEndpoints from '@services/api/endpoints';
import { destroyAuthCookie, cleanupNonEssentialCookies } from '@services/api/utils/authCookie';
import { useMutation, useQuery, UseMutationResult, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { i18n } from 'next-i18next';
import { setCookie } from 'nookies';

export interface LoginCredentials {
  email: string;
  password: string;
}

interface ResetPasswordCredentials {
  token: string;
  password: string;
}

/** The current user: one cache entry (`userQueries.me`), whoever reads it. */
export const useCurrentUser = (): UseQueryResult<UserFront | undefined, Error> => useQuery(userQueries.me());

export const useLogin = (): UseMutationResult<UserFront, Error, LoginCredentials> => {
  const router = useRouter();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, password }: LoginCredentials) => apiEndpoints.auth.login(email, password),
    onSuccess: userData => {
      // Clean legacy/client-side cookies before establishing new session
      cleanupNonEssentialCookies();
      userInvalidations.loggedIn(queryClient, userData);

      const lang = userData.lang === 'cn' ? 'zh' : userData.lang;
      setCookie(null, 'NEXT_LOCALE', lang, {
        maxAge: 30 * 24 * 60 * 60,
        path: '/',
      });

      if (i18n) {
        void i18n.changeLanguage(lang);
      }

      void router.push(getRoute.sstForum(), undefined, { locale: lang });
    },
    meta: {
      skipSuccessToast: true,
    },
  });
};

export const useForgotPassword = (): UseMutationResult<void, Error, string> =>
  useMutation({
    mutationFn: (email: string) => apiEndpoints.user.forgotPassword(email),
    // The form shows the refusal itself (FormApiError); this also mutes the generic error toast.
    meta: { skipSuccessToast: true },
  });

export const useResetPassword = (): UseMutationResult<void, Error, ResetPasswordCredentials> =>
  useMutation({
    mutationFn: ({ token, password }: ResetPasswordCredentials) => apiEndpoints.user.resetPassword(token, password),
    // The form shows the refusal itself (FormApiError); this also mutes the generic error toast.
    meta: { skipSuccessToast: true },
  });

/**
 * useLogout
 * - Calls backend logout endpoint, which expires the HttpOnly auth cookie
 * - If that call fails, expires the cookie through the Next API route instead, so the
 *   middleware cannot bounce /login back to /sst-forum on a still-valid cookie
 * - Navigates to /login before clearing the client cache: once the logged-in layout is
 *   unmounted, no query observer is left to refetch /me with an expired cookie (that
 *   refetch is what made the sidebar name vanish and the full-page spinner flash)
 */
export const useLogout = (): UseMutationResult<void, Error, void> => {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: async () => {
      try {
        await apiEndpoints.auth.logout();
      } catch {
        await fetch('/api/auth/clear-cookie', { method: 'POST', credentials: 'include' }).catch(() => undefined);
      }
    },
    onSuccess: async () => {
      // isPending stays true until this resolves, so the button keeps its spinner until the login page is up
      await router.push('/login');

      destroyAuthCookie();
      queryClient.clear();
      try {
        sessionStorage.clear();
        localStorage.clear();
      } catch {
        // Ignore storage errors (e.g., privacy mode)
      }
    },
    meta: {
      skipSuccessToast: true,
    },
  });
};
